import { CircularProgress, IconButton } from "@material-ui/core";
import { Update } from "@material-ui/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNotify } from "react-admin";
import { handleAxiosError } from "../../../../common/data/axiosErrorHandler";
import {
  HosDetailsDto,
  HosLogsApiFactory,
} from "../../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../../common/hooks/useAxiosInstance";
import { useFleetManagerHubHosUpdates } from "../../../../common/hooks/useFleetManagerHubHosUpdates";
import { HosIndicator } from "../../common/HosIndicator";
import { getHosTimers } from "../../hosUtils";
import { HosErrorIndicator } from "./HosErrorIndicator";

interface Props {
  hosDetails?: HosDetailsDto;
  driverId?: number;
}

export const DriverLogHeaderHosInfo = ({ hosDetails, driverId }: Props) => {
  const [data, setData] = useState<HosDetailsDto>(
    hosDetails ?? {
      maxCycle: 8 * 24 * 60,
      maxDrive: 11 * 60,
      maxShift: 14 * 60,
    }
  );
  const axios = useAxiosInstance();
  const [time, setTime] = useState<Date>(new Date());
  const notify = useNotify();
  const { isPending, isError } = data;

  useFleetManagerHubHosUpdates(driverId);

  useEffect(() => {
    const setTimeEachMinute = () => {
      setTime(new Date());
    };

    const timer = setInterval(setTimeEachMinute, 60000);
    return () => clearInterval(timer);
  }, []);

  // We need to reload the page every 30 seconds while HOS information is pending,
  // as a workaround to the case when the Azure SignalR connection is lost (which happens often).
  // If the SignalR connection is not active at the moment when the "HOS rebuild completed" message is sent,
  // the message gets lost and the reload would never happen.
  useEffect(() => {
    const reload = () => {
      if (isPending && !isError) {
        // console.log("Reloading the page due to HOS information still pending");
        window.location.reload();
      }
    };

    let timeout: NodeJS.Timeout | null = null;
    if (isPending && !isError) {
      // console.log("HOS information is pending, reloading in 30 sec.");
      timeout = setTimeout(reload, 30000);
    } else {
      if (timeout) {
        // console.log(
        //   "HOS information is not pending anymore, aborting the scheduled reload."
        // );
        clearTimeout(timeout);
      } else {
        // console.log(
        //   "HOS information is not yet pending, no need to schedule a reload."
        // );
      }

      timeout = null;
    }
    return () => {
      if (timeout) {
        // console.log(
        //   "Re-rendering due to changed HOS information pending status, aborting the scheduled reload."
        // );
        clearTimeout(timeout);
      }
    };
  }, [isPending, isError]);

  const handleHosInfoRefresh = useCallback(() => {
    if (!axios) {
      return;
    }

    HosLogsApiFactory(undefined, axios.defaults.baseURL, axios)
      .apiAppHoslogsRebuildHosInfoPost(driverId)
      .then((_) => {
        setData((prev) => ({
          ...prev,
          maxDrive: prev?.maxDrive ?? 11 * 60,
          maxCycle: prev?.maxCycle ?? 70 * 60,
          maxShift: prev?.maxShift ?? 14 * 60,
          isPending: true,
          isError: false,
        }));
      })
      .catch((e) => {
        console.error(handleAxiosError(e));
        notify(handleAxiosError(e), { type: "error" });
      });
  }, [axios, driverId, notify]);

  const {
    cycleWork,
    shiftDrive,
    shiftWork,
    regularShiftDrive,
    regularShiftWork,
  } = useMemo(() => {
    const retVal = getHosTimers(data, time);
    return retVal;
  }, [data, time]);

  if (!hosDetails) {
    return null;
  }

  if (!data) {
    return null;
  }

  return (
    <div id="driver-log-header-hos-info">
      {data.isPending ? (
        <div className="loading-indicator">
          <CircularProgress />
        </div>
      ) : null}
      {data.isError ? (
        <HosErrorIndicator onRefresh={handleHosInfoRefresh} />
      ) : null}
      <HosIndicator
        label="Drive"
        valueAsMinutes={regularShiftDrive}
        maxValueAsMinutes={data.maxDrive}
        floater={
          <HosIndicator
            label="SSB"
            valueAsMinutes={shiftDrive}
            maxValueAsMinutes={data.maxDrive}
          />
        }
      />
      <HosIndicator
        label="Shift"
        valueAsMinutes={regularShiftWork}
        maxValueAsMinutes={data.maxShift || 840}
        floater={
          <HosIndicator
            label="SSB"
            valueAsMinutes={shiftWork}
            maxValueAsMinutes={data.maxShift || 840}
          />
        }
      />
      <HosIndicator
        valueAsMinutes={cycleWork}
        maxValueAsMinutes={data.maxCycle || 4200}
        label="Cycle"
      />
      <div className="refresh-button">
        <IconButton onClick={handleHosInfoRefresh}>
          <Update />
        </IconButton>
      </div>
    </div>
  );
};
